import { useMutation, ApolloError } from "@apollo/client";
import { useCallback } from "react";
import { UPDATE_BULK_MESSAGE_MUTATION } from "@/api/mutations/updateBulkMessage";
import {
  UpdateBulkMessageMutation,
  UpdateBulkMessageMutationVariables,
} from "@/__generated__/types";
import { MutationErrorHandler } from "@/util/mutationUtils";
import { FormValues, SubmissionReturn } from "@/pages/BulkMessaging/types";
import { formToUpdateApi } from "@/pages/BulkMessaging/utils/mutationBuilders";
import { studentParser } from "@/pages/BulkMessaging/utils/responseParsers";

interface UpdateBulkMessageReturn {
  loading: boolean;
  error: ApolloError | undefined;
  data: SubmissionReturn;
}

interface UpdateBulkMessageParams {
  bulkMessageId: string;
  formValues: FormValues;
  draft: boolean;
}

export const useUpdateBulkMessage = (): [
  (params: UpdateBulkMessageParams) => void,
  UpdateBulkMessageReturn,
] => {
  const [updateBulkMessageMutation, { loading, data, error }] = useMutation<
    UpdateBulkMessageMutation,
    UpdateBulkMessageMutationVariables
  >(UPDATE_BULK_MESSAGE_MUTATION);

  const updateBulkMessage = useCallback(
    (params: UpdateBulkMessageParams) => {
      const variables = formToUpdateApi(params);
      updateBulkMessageMutation({ variables });
    },
    [updateBulkMessageMutation],
  );

  const errorHandler =
    data && MutationErrorHandler.fromResult({ data }, error?.graphQLErrors);
  const bulkMessage = data?.updateBulkMessageMutation?.bulkMessage;

  const returnData = {
    result: errorHandler?.toResult(),
    draft: bulkMessage?.draft,
    audienceReach: {
      students: studentParser(bulkMessage?.audienceReach),
      totalCount: bulkMessage?.audienceReach.totalCount || 0,
    },
  };

  return [updateBulkMessage, { loading, data: returnData, error }];
};
